import * as pc from 'playcanvas';
import { box, colorMaterial } from './app';
import { spawnCharacter } from './character';
import type { Obstacle } from './world';

// Human-scale locomotion (the old 8/15 arcade speeds made the animated
// character's legs read as comically slow foot-sliding).
const WALK_SPEED = 4.5;
const SPRINT_SPEED = 9;

const EYE_HEIGHT = 1.68;
const CAM_HEIGHT = 5.5;

const THIRD_DIST = { min: 5, max: 26, initial: 11 };
const HELI_ALT = { min: 25, max: 140, initial: 60 };
const FP_FOV = { min: 25, max: 70, initial: 60 };
const DEFAULT_FOV = 60;

export type ViewMode = 'first' | 'third' | 'heli';
const MODE_ORDER: readonly ViewMode[] = ['first', 'third', 'heli'];

export interface TouchInput {
  /** Movement axis from the virtual joystick, both in [-1, 1]. */
  getAxis(): { x: number; y: number };
  /** Accumulated look delta since last frame (pixels). */
  consumeLookDelta(): { x: number; y: number };
}

export class Player {
  readonly entity: pc.Entity;
  private yaw = 180;
  private pitch = -4;
  private dragging = false;
  private mode: ViewMode = 'first';

  // Per-mode zoom state (mouse wheel / HUD ± buttons)
  private thirdDist = THIRD_DIST.initial;
  private heliAlt = HELI_ALT.initial;
  private fpFov = FP_FOV.initial;

  /** Instant placeholder body, swapped out once the Mixamo character streams in. */
  private readonly bodyParts: pc.Entity[] = [];
  private character: pc.Entity | null = null;
  /** Seconds since the last manual look input — gates the third-person auto-follow. */
  private lookIdle = 10;
  /** Last-frame movement state, read by the audio footstep scheduler / minimap. */
  private movingNow = false;
  private sprintingNow = false;
  /** Smoothed camera position for third/heli (first-person stays raw). */
  private readonly camSmooth = new pc.Vec3();
  private camSmoothReady = false;
  /** Subtle first-person walk bob. */
  private bobPhase = 0;
  private bobAmp = 0;

  onModeChanged: ((mode: ViewMode) => void) | null = null;

  constructor(
    app: pc.Application,
    private readonly keyboard: pc.Keyboard,
    private readonly camera: pc.Entity,
    private readonly obstacles: Obstacle[],
    private readonly touch: TouchInput | null,
  ) {
    this.entity = new pc.Entity('player');
    const bodyMat = colorMaterial('#2f6fde');
    const body = box('body', { x: 1, y: 1.6, z: 0.6 }, { x: 0, y: 0.8, z: 0 }, bodyMat);
    const head = box('head', { x: 0.7, y: 0.7, z: 0.7 }, { x: 0, y: 1.95, z: 0 }, colorMaterial('#f0c8a0'));
    this.entity.addChild(body);
    this.entity.addChild(head);
    this.bodyParts.push(body, head);
    this.entity.setPosition(0, 0, 24);
    app.root.addChild(this.entity);
    this.applyModeVisibility();
    this.loadCharacter(app);

    if (app.mouse) {
      app.mouse.on(pc.EVENT_MOUSEDOWN, (e: pc.MouseEvent) => {
        if (e.button === pc.MOUSEBUTTON_LEFT || e.button === pc.MOUSEBUTTON_RIGHT) {
          this.dragging = true;
        }
      });
      app.mouse.on(pc.EVENT_MOUSEUP, () => {
        this.dragging = false;
      });
      app.mouse.on(pc.EVENT_MOUSEMOVE, (e: pc.MouseEvent) => {
        if (this.dragging) {
          this.applyLook(e.dx, e.dy);
        }
      });
      app.mouse.on(pc.EVENT_MOUSEWHEEL, (e: pc.MouseEvent) => {
        this.zoom(e.wheelDelta > 0 ? 1 : -1);
      });
    }
  }

  /** Mixamo rigged character (public/character/player.glb, forward-root-motion
   * stripped so it walks in place — the entity supplies world movement). */
  private loadCharacter(app: pc.Application): void {
    void spawnCharacter(app).then((inst) => {
      if (!inst) return;
      // speed 0 (not pause) — pausing before the first evaluation freezes the
      // bind T-pose; speed 0 keeps evaluating the clip's frame-0 idle stance.
      inst.anim.speed = 0;
      this.entity.addChild(inst.entity);
      this.character = inst.entity;
      this.applyModeVisibility();
    });
  }

  getMode(): ViewMode {
    return this.mode;
  }

  setMode(mode: ViewMode): void {
    if (this.mode === mode) return;
    this.mode = mode;
    this.applyLook(0, 0); // re-clamp pitch into the new mode's range
    this.applyModeVisibility();
    // Seed the smoothing from wherever the camera is now, so switching modes
    // glides instead of snapping.
    this.camSmooth.copy(this.camera.getPosition());
    this.camSmoothReady = true;
    this.updateCamera();
    this.onModeChanged?.(mode);
  }

  toggleMode(): void {
    const next = MODE_ORDER[(MODE_ORDER.indexOf(this.mode) + 1) % MODE_ORDER.length];
    this.setMode(next);
  }

  /** Positive steps zoom in; wired to the mouse wheel and the HUD ± buttons. */
  zoom(steps: number): void {
    if (this.mode === 'first') {
      this.fpFov = pc.math.clamp(this.fpFov - steps * 5, FP_FOV.min, FP_FOV.max);
    } else if (this.mode === 'third') {
      this.thirdDist = pc.math.clamp(this.thirdDist - steps * 2, THIRD_DIST.min, THIRD_DIST.max);
    } else {
      this.heliAlt = pc.math.clamp(this.heliAlt - steps * 10, HELI_ALT.min, HELI_ALT.max);
    }
    this.updateCamera();
  }

  /** Test/measurement hook: point the view somewhere specific. */
  setLook(yaw: number, pitch: number): void {
    this.yaw = yaw;
    this.pitch = pitch;
    this.applyLook(0, 0);
    this.updateCamera();
  }

  private applyModeVisibility(): void {
    const showBody = this.mode !== 'first';
    for (const part of this.bodyParts) {
      part.enabled = showBody && !this.character;
    }
    if (this.character) {
      this.character.enabled = showBody;
    }
  }

  private applyLook(dx: number, dy: number): void {
    if (dx !== 0 || dy !== 0) {
      this.lookIdle = 0; // manual look wins over auto-follow for a moment
    }
    this.yaw -= dx * 0.25;
    const range =
      this.mode === 'first' ? { min: -80, max: 80 } : { min: -60, max: 10 };
    this.pitch = pc.math.clamp(this.pitch - dy * 0.2, range.min, range.max);
  }

  update(dt: number): void {
    // --- Look (touch) ---
    if (this.touch) {
      const look = this.touch.consumeLookDelta();
      if (look.x !== 0 || look.y !== 0) {
        this.applyLook(look.x, look.y);
      }
    }

    // --- Movement input ---
    let inputX = 0;
    let inputZ = 0;
    if (this.keyboard.isPressed(pc.KEY_W) || this.keyboard.isPressed(pc.KEY_UP)) inputZ -= 1;
    if (this.keyboard.isPressed(pc.KEY_S) || this.keyboard.isPressed(pc.KEY_DOWN)) inputZ += 1;
    if (this.keyboard.isPressed(pc.KEY_A) || this.keyboard.isPressed(pc.KEY_LEFT)) inputX -= 1;
    if (this.keyboard.isPressed(pc.KEY_D) || this.keyboard.isPressed(pc.KEY_RIGHT)) inputX += 1;

    if (this.touch) {
      const axis = this.touch.getAxis();
      inputX += axis.x;
      inputZ += axis.y;
    }

    const length = Math.hypot(inputX, inputZ);
    if (length > 1) {
      inputX /= length;
      inputZ /= length;
    }

    const sprinting = this.keyboard.isPressed(pc.KEY_SHIFT);
    const speed = sprinting ? SPRINT_SPEED : WALK_SPEED;
    const moving = length > 0.01;

    this.lookIdle += dt;

    if (moving) {
      // View-relative in every mode: W always walks where the camera looks
      // (view forward = (-sin, -cos)); A/D strafe relative to that.
      const yawRad = (this.yaw * Math.PI) / 180;
      const dirX = inputX * Math.cos(yawRad) + inputZ * Math.sin(yawRad);
      const dirZ = -inputX * Math.sin(yawRad) + inputZ * Math.cos(yawRad);

      const pos = this.entity.getPosition();
      const nextX = pos.x + dirX * speed * dt;
      const nextZ = pos.z + dirZ * speed * dt;

      // Axis-separated collision so the player slides along walls
      const blockedX = this.collides(nextX, pos.z);
      const blockedZ = this.collides(blockedX ? pos.x : nextX, nextZ);
      this.entity.setPosition(blockedX ? pos.x : nextX, 0, blockedZ ? pos.z : nextZ);

      // Face movement direction
      const targetAngle = (Math.atan2(dirX, dirZ) * 180) / Math.PI;
      this.entity.setEulerAngles(0, targetAngle, 0);

      // Third-person follow camera: ease the camera around behind the new
      // heading (GTA-style) unless the user is actively steering the view.
      if (this.mode === 'third' && !this.dragging && this.lookIdle > 0.4) {
        const desired = (Math.atan2(-dirX, -dirZ) * 180) / Math.PI;
        const delta = ((desired - this.yaw + 540) % 360) - 180;
        // ~2/s catch-up: fast enough to settle behind the player in under a
        // second, slow enough that steering carves a wide arc instead of a
        // tight circle.
        this.yaw += delta * Math.min(1, dt * 2);
      }
    }

    this.movingNow = moving;
    this.sprintingNow = sprinting;

    // First-person walk bob: phase tracks the footstep cadence, amplitude
    // eases in/out so stopping never jolts the view.
    this.bobPhase += dt * (sprinting ? 13 : 9);
    const targetAmp = moving && this.mode === 'first' ? 0.035 : 0;
    this.bobAmp += (targetAmp - this.bobAmp) * Math.min(1, dt * 6);

    this.driveAnimation(moving, sprinting);
    this.updateCamera(dt);
  }

  get isMoving(): boolean {
    return this.movingNow;
  }

  get isSprinting(): boolean {
    return this.sprintingNow;
  }

  /** Camera yaw θ (view forward = (−sinθ, −cosθ)) — used by the minimap arrow. */
  get heading(): number {
    return this.yaw;
  }

  private driveAnimation(moving: boolean, sprinting: boolean): void {
    const anim = this.character?.anim;
    if (!anim) return;
    anim.speed = moving ? (sprinting ? 2.2 : 1.5) : 0;
  }

  private collides(x: number, z: number): boolean {
    for (const o of this.obstacles) {
      if (x > o.minX && x < o.maxX && z > o.minZ && z < o.maxZ) {
        return true;
      }
    }
    return false;
  }

  private updateCamera(dt = 1 / 60): void {
    const pos = this.entity.getPosition();
    if (this.camera.camera) {
      this.camera.camera.fov = this.mode === 'first' ? this.fpFov : DEFAULT_FOV;
    }

    if (this.mode === 'first') {
      // Raw (unsmoothed) so the view never swims; bob adds walking presence.
      const bobY = Math.sin(this.bobPhase) * this.bobAmp;
      const yawRad = (this.yaw * Math.PI) / 180;
      const sway = Math.sin(this.bobPhase * 0.5) * this.bobAmp * 0.6;
      this.camera.setPosition(
        pos.x + Math.cos(yawRad) * sway,
        pos.y + EYE_HEIGHT + bobY,
        pos.z - Math.sin(yawRad) * sway,
      );
      this.camera.setEulerAngles(this.pitch, this.yaw, 0);
      return;
    }

    const yawRad = (this.yaw * Math.PI) / 180;
    const target = new pc.Vec3();
    const lookY = this.mode === 'heli' ? 0 : pos.y + 2;

    if (this.mode === 'heli') {
      const d = this.heliAlt * 0.35;
      target.set(pos.x + d * Math.sin(yawRad), this.heliAlt, pos.z + d * Math.cos(yawRad));
    } else {
      const pitchRad = (this.pitch * Math.PI) / 180;
      const horizontal = this.thirdDist * Math.cos(pitchRad);
      target.set(
        pos.x + horizontal * Math.sin(yawRad),
        pos.y + CAM_HEIGHT * (this.thirdDist / THIRD_DIST.initial) - this.thirdDist * Math.sin(pitchRad) * 0.6,
        pos.z + horizontal * Math.cos(yawRad),
      );
      // Pull the camera in front of any building between it and the player.
      const t = this.cameraClearance(pos.x, pos.z, target.x, target.z) * 0.92;
      if (t < 1) {
        const pivotY = pos.y + 2;
        target.set(
          pos.x + (target.x - pos.x) * t,
          pivotY + (target.y - pivotY) * Math.max(t, 0.35),
          pos.z + (target.z - pos.z) * t,
        );
      }
    }

    if (!this.camSmoothReady) {
      this.camSmooth.copy(target);
      this.camSmoothReady = true;
    } else {
      this.camSmooth.lerp(this.camSmooth, target, Math.min(1, dt * 10));
    }
    this.camera.setPosition(this.camSmooth);
    this.camera.lookAt(pos.x, lookY, pos.z);
  }

  /** First hit parameter t∈(0,1] of the pivot→camera segment against the world's
   * obstacle AABBs (2D slab test — buildings are tall enough to ignore height). */
  private cameraClearance(px: number, pz: number, tx: number, tz: number): number {
    const dx = tx - px;
    const dz = tz - pz;
    let tMin = 1;
    for (const o of this.obstacles) {
      // Low obstacles (plot walls) never block the camera — it sees over them.
      if (o.maxY !== undefined && o.maxY < 3) continue;
      let t0 = 0;
      let t1 = 1;
      if (Math.abs(dx) < 1e-6) {
        if (px < o.minX || px > o.maxX) continue;
      } else {
        let a = (o.minX - px) / dx;
        let b = (o.maxX - px) / dx;
        if (a > b) [a, b] = [b, a];
        t0 = Math.max(t0, a);
        t1 = Math.min(t1, b);
      }
      if (Math.abs(dz) < 1e-6) {
        if (pz < o.minZ || pz > o.maxZ) continue;
      } else {
        let a = (o.minZ - pz) / dz;
        let b = (o.maxZ - pz) / dz;
        if (a > b) [a, b] = [b, a];
        t0 = Math.max(t0, a);
        t1 = Math.min(t1, b);
      }
      if (t0 <= t1 && t0 > 0.01 && t0 < tMin) {
        tMin = t0;
      }
    }
    return tMin;
  }

  get position(): pc.Vec3 {
    return this.entity.getPosition();
  }
}
